import React, { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";

/**
 * Simple deterministic self-check. No animations. Result depends on income
 * bucket and situation to produce one of three heuristic outputs.
 */
export default function EligibilityCheck() {
  const { t } = useI18n();
  const [q1, setQ1] = useState("");
  const [q2, setQ2] = useState("");
  const [q3, setQ3] = useState("");
  const [result, setResult] = useState(null);

  const compute = () => {
    if (!q1 || !q2 || !q3) return;
    let score = 0;
    if (q1 === "1") score += 3;
    if (q1 === "2") score += 2;
    if (q1 === "3") score += 1;
    if (q2 === "3" || q2 === "4") score += 1;
    if (q3 === "3" || q3 === "4") score += 2;
    if (q3 === "2") score += 1;
    if (score >= 4) setResult("high");
    else if (score >= 2) setResult("medium");
    else setResult("low");
  };

  const reset = () => {
    setQ1("");
    setQ2("");
    setQ3("");
    setResult(null);
  };

  const opts = (key, count) =>
    Array.from({ length: count }, (_, i) => ({
      value: String(i + 1),
      label: t(`eligibility.${key}_opt${i + 1}`),
    }));

  return (
    <div
      id="elegibilidad"
      data-testid="eligibility-widget"
      className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <p className="text-xs font-bold uppercase tracking-widest text-[#059669]">
        {t("eligibility.eyebrow")}
      </p>
      <h3 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
        {t("eligibility.title")}
      </h3>
      <p className="mt-2 text-slate-600">{t("eligibility.subtitle")}</p>

      <div className="mt-6 grid gap-5 md:grid-cols-3 md:items-end">
        {[
          { key: "q1", state: q1, setter: setQ1, count: 4 },
          { key: "q2", state: q2, setter: setQ2, count: 4 },
          { key: "q3", state: q3, setter: setQ3, count: 4 },
        ].map((cfg) => (
          <div key={cfg.key} className="flex flex-col">
            <p className="min-h-[3rem] text-sm font-semibold text-slate-800 leading-snug">
              {t(`eligibility.${cfg.key}`)}
            </p>
            <Select value={cfg.state} onValueChange={cfg.setter}>
              <SelectTrigger
                className="mt-2 h-11 w-full bg-white text-slate-900"
                data-testid={`eligibility-${cfg.key}`}
              >
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {opts(cfg.key, cfg.count).map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          onClick={compute}
          data-testid="eligibility-check"
          className="bg-[#0F2942] hover:bg-[#1E3A5F] text-white rounded-full px-6 h-11"
        >
          {t("eligibility.check")}
        </Button>
        <Button
          onClick={reset}
          data-testid="eligibility-reset"
          variant="outline"
          className="rounded-full px-6 h-11 border-slate-300"
        >
          {t("eligibility.reset")}
        </Button>
      </div>

      {result && (
        <div
          data-testid="eligibility-result"
          className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5"
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 text-[#047857]" size={22} />
            <div>
              <p className="font-semibold text-emerald-900">
                {t("eligibility.result_title")}
              </p>
              <p className="mt-1 text-sm text-emerald-900/90">
                {t(`eligibility.result_${result}`)}
              </p>
              <a
                href="#newsletter"
                data-testid="eligibility-cta"
                className="mt-3 inline-block text-sm font-semibold text-[#047857] underline underline-offset-2"
              >
                {t("eligibility.result_cta")} →
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
